import React, { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { parseISO } from "date-fns";
import SavvytronFooter from '../../../components/SavvytronFooter';
import { isCupcakeProductPreset } from "../utils/orderFormLinks";
import { getServerUrl } from "../../../utils/serverUrl";
import {
  ALL_SLOTS,
  CUPCAKE_SLOTS,
  getEarliestPickupDate,
  getTimeSlotsForDate,
  shouldBlockDate,
} from "../utils/pickupSchedule";

export default function StepOneCustomerInfo({
  formData,
  setFormData,
  nextStep,
  summerTimingEnabled = false,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const isCupcakeFlow = isCupcakeProductPreset(new URLSearchParams(location.search));
  const SERVER_URL = getServerUrl();

  useEffect(() => {
    if (SERVER_URL) {
      fetch(SERVER_URL, { method: "GET" }).catch(() => { });
    }
  }, [SERVER_URL]);

  const scheduleOptions = useMemo(
    () => ({ summerTimingEnabled, isCupcakeFlow }),
    [summerTimingEnabled, isCupcakeFlow]
  );

  const earliestPickup = useMemo(
    () => getEarliestPickupDate({ summerTimingEnabled }),
    [summerTimingEnabled]
  );

  const resolveSlots = (date) => {
    const slots = getTimeSlotsForDate(date, {
      ...scheduleOptions,
      earliestPickup,
    });
    const fallback = isCupcakeFlow ? CUPCAKE_SLOTS : ALL_SLOTS;
    return slots.length > 0 ? slots : fallback;
  };

  const [availableSlots, setAvailableSlots] = useState([]);
  const [error, setError] = useState("");
  const hasPickupDate = Boolean(formData.eventDate);

  useEffect(() => {
    if (formData.eventDate) {
      setAvailableSlots(resolveSlots(parseISO(formData.eventDate)));
    } else {
      setAvailableSlots([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.eventDate, summerTimingEnabled, isCupcakeFlow, earliestPickup]);

  const validateForm = () => {
    const { firstName, lastName, customerPhone, customerEmail, eventDate, eventTime } = formData;
    return (
      firstName.trim() &&
      lastName.trim() &&
      customerPhone.trim() &&
      customerEmail.trim() &&
      eventDate &&
      eventTime
    );
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date) => {
    if (!date) {
      setFormData((prev) => ({ ...prev, eventDate: "", eventTime: "" }));
      setAvailableSlots([]);
      return;
    }

    const iso = date.toISOString().split("T")[0];
    const finalSlots = resolveSlots(date);
    setAvailableSlots(finalSlots);
    setFormData((prev) => ({
      ...prev,
      eventDate: iso,
      eventTime: finalSlots.includes(prev.eventTime) ? prev.eventTime : "",
    }));
  };

  return (
    <div className="space-y-10 max-w-3xl mx-auto px-4">
      <h2 className="text-center text-xl sm:text-2xl font-semibold text-candy-text font-hobo">
        Customer Information
      </h2>

      {summerTimingEnabled && (
        <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-center leading-snug">
          Summer hours: closed Sundays &amp; Mondays. Tuesday &amp; Wednesday pickups end at 4:00 PM.
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <input type="text" name="firstName" placeholder="First Name" value={formData.firstName} onChange={handleFieldChange} required className="w-full p-3 border border-candy-border rounded-lg focus:outline-none focus:ring-2 focus:ring-candy-primary bg-white placeholder-gray-400" />
        <input type="text" name="lastName" placeholder="Last Name" value={formData.lastName} onChange={handleFieldChange} required className="w-full p-3 border border-candy-border rounded-lg focus:outline-none focus:ring-2 focus:ring-candy-primary bg-white placeholder-gray-400" />
        <input type="text" name="customerPhone" placeholder="Phone Number" value={formData.customerPhone} onChange={handleFieldChange} required className="w-full p-3 border border-candy-border rounded-lg focus:outline-none focus:ring-2 focus:ring-candy-primary bg-white placeholder-gray-400" />
        <input type="email" name="customerEmail" placeholder="Your Email" value={formData.customerEmail} onChange={handleFieldChange} required className="w-full p-3 border border-candy-border rounded-lg focus:outline-none focus:ring-2 focus:ring-candy-primary bg-white placeholder-gray-400" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div className="w-[188px]">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Pickup Date
          </label>
          <DatePicker
            selected={formData.eventDate ? parseISO(formData.eventDate) : null}
            onChange={handleDateChange}
            minDate={earliestPickup}
            dateFormat="dd-MM-yyyy"
            placeholderText="Select a date"
            filterDate={(d) => !shouldBlockDate(d, { summerTimingEnabled, earliestPickup })}
            className="w-full p-3 border border-candy-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-candy-primary bg-white shadow-sm placeholder-gray-400"
          />
        </div>

        <div className="w-[188px]">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Pickup Time
          </label>
          <div className="relative">
            <select
              name="eventTime"
              value={formData.eventTime}
              onChange={(e) => setFormData((prev) => ({ ...prev, eventTime: e.target.value }))}
              required
              disabled={!hasPickupDate}
              className={`appearance-none w-full p-3 border rounded-lg text-sm shadow-sm ${
                hasPickupDate
                  ? "border-candy-border focus:outline-none focus:ring-2 focus:ring-candy-primary bg-white"
                  : "border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed"
              } ${hasPickupDate && !formData.eventTime ? "text-gray-400" : hasPickupDate ? "text-gray-900" : ""}`}
            >
              <option value="" disabled hidden>
                {hasPickupDate ? "Select Pickup Time" : "Select a date first"}
              </option>
              {availableSlots.map((label) => (
                <option key={label} value={label}>{label}</option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-400">▼</div>
          </div>
          {!hasPickupDate && (
            <p className="text-xs text-gray-500 mt-1.5 leading-snug">Choose a pickup date to see available times.</p>
          )}
        </div>
      </div>

      {error && <div className="text-red-600 text-sm font-medium text-center mb-4">{error}</div>}

      <div className="flex justify-between items-center pt-6">
        <button onClick={() => navigate("/")} className="text-candy-primary font-semibold flex items-center gap-2 transition-transform transform hover:scale-105">← 🏠 Home</button>
        <button
          type="button"
          onClick={() => {
            if (validateForm()) {
              setError("");
              nextStep();
            } else {
              setError("Please fill in all fields before proceeding.");
            }
          }}
          className="bg-candy-primary hover:bg-candy-primaryDark text-white font-semibold px-6 py-2 rounded-md transition"
        >
          Next ➜
        </button>
      </div>

      <SavvytronFooter />
    </div>
  );
}
